// src/viqai/AdSlotsAdmin.jsx
// ─── Admin › Acquisition › Emplacements publicitaires (§16-18, §88-89) ──────
// Deux slots, pas un gestionnaire de campagnes. Chaque champ correspond
// exactement à une colonne de public_ad_slots : image desktop, image mobile,
// URL de destination, texte alternatif, titre interne, actif/inactif.
//
// §19-20 — La validation est refaite côté serveur. Celle-ci n'est qu'un confort :
// prévenir avant l'envoi plutôt qu'après.

import { useState, useEffect, useCallback } from "react";
import { apiGet, apiPatch, apiDelete } from "./api";
import "./viqai-dark.css";

const MAX_BYTES = 1024 * 1024;
const MIME_OK = ["image/jpeg", "image/png", "image/webp"];

function readImage(file) {
  return new Promise((resolve, reject) => {
    if (!MIME_OK.includes(file.type)) return reject(new Error("Format non autorisé (JPG, PNG ou WebP)."));
    if (file.size > MAX_BYTES) return reject(new Error(`Image trop lourde (${Math.round(file.size / 1024)} Ko, maximum 1 Mo).`));
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = () => reject(new Error("Lecture du fichier impossible."));
    r.readAsDataURL(file);
  });
}

function SlotEditor({ slot, constraints, onSaved }) {
  const [form, setForm] = useState({
    title: slot.title || "", target_url: slot.target_url || "", alt_text: slot.alt_text || "",
    desktop_image_url: slot.desktop_image_url || "", mobile_image_url: slot.mobile_image_url || "",
    is_active: !!slot.is_active,
  });
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const pick = (which) => async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    setError(null);
    try { const data = await readImage(f); setForm(x => ({ ...x, [which]: data })); }
    catch (err) { setError(err.message); }
  };

  const save = async () => {
    setBusy(true); setError(null);
    const j = await apiPatch(`/api/admin/ad-slots/${slot.slot_key}`, form).catch(e => ({ error: e.message }));
    setBusy(false);
    if (j?.success) onSaved(); else setError(j?.error || "Enregistrement impossible.");
  };

  const remove = async () => {
    if (!window.confirm("Retirer l'annonce de cet emplacement ? L'emplacement restera configurable.")) return;
    setBusy(true);
    const j = await apiDelete(`/api/admin/ad-slots/${slot.slot_key}`).catch(() => null);
    setBusy(false);
    if (j?.success) onSaved();
  };

  return (
    <div className="aff-card">
      <div className="aff-card-head">
        <span className="aff-name">{slot.slot_key === "lead_left" ? "Lead page — gauche" : "Lead page — droite"}</span>
        <span className={`viq-chip${form.is_active ? " accent" : ""}`}>{form.is_active ? "Actif" : "Inactif"}</span>
      </div>

      {/* §18 — Les contraintes viennent du serveur et sont affichées telles quelles. */}
      <div className="aff-hint" style={{ marginBottom: ".8rem" }}>
        Format recommandé : <strong>{constraints.desktop}</strong> · {constraints.formats} · max {constraints.maxSize}.
        Mobile (optionnel) : <strong>{constraints.mobile}</strong>.
      </div>

      <div className="aff-grid2">
        <label className="aff-field"><span className="aff-label">Titre interne</span>
          <input value={form.title} onChange={set("title")} placeholder="Partenaire — campagne septembre" /></label>
        <label className="aff-field"><span className="aff-label">URL de destination</span>
          <input value={form.target_url} onChange={set("target_url")} placeholder="https://exemple.fr/offre" /></label>
        <label className="aff-field"><span className="aff-label">Texte alternatif</span>
          <input value={form.alt_text} onChange={set("alt_text")} placeholder="Offre partenaire — financement VO" /></label>
      </div>

      <div className="aff-sub">Aperçu desktop</div>
      <div className="ad-preview">
        {form.desktop_image_url
          ? <img src={form.desktop_image_url} alt="" className="ad-preview-desktop" />
          : <div className="ad-preview-empty ad-preview-desktop">300 × 600</div>}
        <div>
          <input type="file" accept="image/jpeg,image/png,image/webp" onChange={pick("desktop_image_url")} />
          {form.desktop_image_url && (
            <button type="button" className="viq-btn" style={{ marginTop: ".5rem" }}
              onClick={() => setForm(f => ({ ...f, desktop_image_url: "" }))}>Retirer l'image</button>
          )}
        </div>
      </div>

      <div className="aff-sub">Aperçu mobile</div>
      <div className="ad-preview">
        {form.mobile_image_url
          ? <img src={form.mobile_image_url} alt="" className="ad-preview-mobile" />
          : <div className="ad-preview-empty ad-preview-mobile">320 × 100</div>}
        <input type="file" accept="image/jpeg,image/png,image/webp" onChange={pick("mobile_image_url")} />
      </div>

      <label className="aff-check">
        <input type="checkbox" checked={form.is_active} onChange={set("is_active")} />
        Afficher cet emplacement sur /leads
      </label>

      {error && <div className="aff-error">{error}</div>}
      <div className="viq-actions">
        <button className="viq-btn primary" disabled={busy} onClick={save}>{busy ? "Enregistrement…" : "Enregistrer"}</button>
        {(slot.desktop_image_url || slot.mobile_image_url) && (
          <button className="viq-btn" disabled={busy} onClick={remove}>Retirer l'annonce</button>
        )}
      </div>
    </div>
  );
}

export default function AdSlotsAdmin({ onClose }) {
  const [data, setData] = useState(null);
  const load = useCallback(() => {
    apiGet("/api/admin/ad-slots").then(j => { if (j?.success) setData(j); }).catch(() => setData({ slots: [], constraints: {} }));
  }, []);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="aff-admin viq-themed">
      <div className="aff-head">
        <h2>Emplacements publicitaires</h2>
        {onClose && <button className="viq-tool-btn" onClick={onClose} aria-label="Fermer">✕</button>}
      </div>
      <div className="aff-hint" style={{ marginBottom: "1rem" }}>
        Deux emplacements encadrent le formulaire de la page <strong>/leads</strong>. Ils n'apparaissent
        qu'à partir de 1280 px de large ; en dessous, le formulaire reste prioritaire. Un emplacement
        inactif n'affiche rien — la page se recentre d'elle-même.
      </div>
      {!data && <div className="viq-empty">Chargement…</div>}
      {data?.slots?.map(s => (
        <SlotEditor key={s.slot_key} slot={s} constraints={data.constraints || {}} onSaved={load} />
      ))}
    </div>
  );
}
